import React from 'react';
import { OKRState } from '../types';
import { computeSmartScore } from '../helpers';
import { useOKRPermissions } from '../useOKRPermissions';
import { CheckCircle2, Clock, Zap, TrendingUp } from 'lucide-react';

const TeamPanel: React.FC<{ state: OKRState }> = ({ state }) => {
    const { canSeeTeamDetails, currentUserId, currentUserName } = useOKRPermissions();
    const { team, tasks } = state;

    // Non-owners see only their own card
    const visibleTeam = canSeeTeamDetails
        ? team
        : team.filter((m) =>
              m.id === currentUserId ||
              m.name === currentUserName ||
              (currentUserId === '' && team.indexOf(m) === 0),
          );

    return (
        <div className="space-y-6">
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5">
                <h3 className="font-bold text-navy-700 mb-1">Performance de l'équipe</h3>
                <p className="text-xs text-gray-400 mb-6">Le score mesure la contribution aux objectifs (moyenne des smart scores des tâches terminées), pas le volume pur.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {visibleTeam.map((member) => {
                        const memberTasks = tasks.filter((t) => t.assigneeId === member.id);
                        const done = memberTasks.filter((t) => t.status === 'done');
                        const contributionScore = done.length > 0 ? Math.round(done.reduce((s, t) => s + computeSmartScore(t), 0) / done.length * 10) / 10 : 0;

                        // Weighted performance score
                        const perfScore = Math.round(member.onTimeRate * 0.3 + member.completionRate * 0.3 + Math.min(100, contributionScore * 5) * 0.4);
                        const scoreColor = perfScore >= 80 ? '#17ad37' : perfScore >= 60 ? '#FFB547' : '#ea0606';
                        const loadPct = Math.min(100, (member.currentActiveTasks / 8) * 100);
                        const loadColor = loadPct >= 80 ? '#ea0606' : loadPct >= 60 ? '#FFB547' : '#17ad37';

                        return (
                            <div key={member.id} className="rounded-2xl border border-gray-100 bg-gradient-to-b from-white to-gray-50/50 p-5 flex flex-col gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-base font-extrabold shadow-sm flex-shrink-0" style={{ backgroundColor: member.avatarColor }}>{member.avatarInitials}</div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-bold text-navy-700">{member.name}</p>
                                        <p className="text-xs text-gray-400">{member.role}</p>
                                    </div>
                                    <div className="text-center ml-auto">
                                        <p className="text-xl font-extrabold" style={{ color: scoreColor }}>{perfScore}</p>
                                        <p className="text-xs text-gray-400">score</p>
                                    </div>
                                </div>

                                <div className="space-y-2.5">
                                    {[
                                        { label: 'Tâches terminées', value: member.tasksCompleted, icon: <CheckCircle2 size={11} />, bar: false },
                                        { label: 'Respect des délais', value: `${member.onTimeRate}%`, icon: <Clock size={11} />, bar: true, pct: member.onTimeRate, barColor: member.onTimeRate >= 90 ? '#17ad37' : '#FFB547' },
                                        { label: 'Taux d\'achèvement', value: `${member.completionRate}%`, icon: <Zap size={11} />, bar: true, pct: member.completionRate, barColor: '#0260CB' },
                                        { label: 'Charge actuelle', value: `${member.currentActiveTasks} tâches`, icon: <TrendingUp size={11} />, bar: true, pct: loadPct, barColor: loadColor },
                                    ].map(({ label, value, icon, bar, pct, barColor }) => (
                                        <div key={label}>
                                            <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
                                                <span className="flex items-center gap-1">{icon} {label}</span>
                                                <span className="font-bold text-navy-700">{value}</span>
                                            </div>
                                            {bar && pct !== undefined && barColor && (
                                                <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <div className="flex justify-between text-xs pt-1 border-t border-gray-100 text-gray-500">
                                        <span>Temps moyen / tâche</span><span className="font-medium text-navy-700">{member.avgCompletionHours}h</span>
                                    </div>
                                    <div className="flex justify-between text-xs text-gray-500">
                                        <span>Contribution score (smart)</span><span className="font-medium text-toola-500">{contributionScore}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default TeamPanel;
